"use client";

import { c as _c } from "react/compiler-runtime";
import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area";
import { cn } from "@workspace/ui/lib/utils";
import { Children, isValidElement, type ReactNode } from "react";
interface ScrollAreaProps extends ScrollAreaPrimitive.Root.Props {
  /** Apply scrollbar visibility only on hover/scroll */
  autoHide?: boolean;
  /** Enable horizontal scrollbar (only used when no custom ScrollBar children) */
  horizontal?: boolean;
}
function ScrollArea(t0) {
  const $ = _c(28);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      children,
      horizontal: t1,
      autoHide: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const horizontal = t1 === undefined ? false : t1;
  const autoHide = t2 === undefined ? true : t2;
  let contentChildren;
  let customHorizontalScrollbar;
  let customVerticalScrollbar;
  if ($[6] !== children) {
    const childArray = Children.toArray(children);
    contentChildren = [];
    customVerticalScrollbar = null;
    customHorizontalScrollbar = null;
    childArray.forEach(child => {
      if (isValidElement(child)) {
        if (child.type === ScrollBar) {
          const scrollbarProps = child.props as ScrollBarProps;
          const orientation = scrollbarProps.orientation ?? "vertical";
          if (orientation === "vertical") {
            customVerticalScrollbar = child;
          } else {
            customHorizontalScrollbar = child;
          }
        } else {
          contentChildren.push(child);
        }
      } else {
        contentChildren.push(child);
      }
    });
    $[6] = children;
    $[7] = contentChildren;
    $[8] = customHorizontalScrollbar;
    $[9] = customVerticalScrollbar;
  } else {
    contentChildren = $[7];
    customHorizontalScrollbar = $[8];
    customVerticalScrollbar = $[9];
  }
  let t3;
  if ($[10] !== autoHide || $[11] !== customVerticalScrollbar) {
    t3 = customVerticalScrollbar ?? <ScrollBar autoHide={autoHide} orientation="vertical" />;
    $[10] = autoHide;
    $[11] = customVerticalScrollbar;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const verticalScrollbar = t3;
  let t4;
  if ($[13] !== autoHide || $[14] !== customHorizontalScrollbar || $[15] !== horizontal) {
    t4 = customHorizontalScrollbar ?? (horizontal ? <ScrollBar autoHide={autoHide} orientation="horizontal" /> : null);
    $[13] = autoHide;
    $[14] = customHorizontalScrollbar;
    $[15] = horizontal;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const horizontalScrollbar = t4;
  let t5;
  if ($[17] !== className) {
    t5 = cn("relative", className);
    $[17] = className;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const t6 = horizontalScrollbar ? undefined : "min-w-0!";
  let t7;
  if ($[19] !== contentChildren || $[20] !== t6) {
    t7 = <ScrollAreaPrimitive.Viewport className="focus-visible:ring-ring/50 size-full max-h-[inherit] rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1" data-slot="scroll-area-viewport"><ScrollAreaPrimitive.Content className={t6} data-slot="scroll-area-content">{contentChildren}</ScrollAreaPrimitive.Content></ScrollAreaPrimitive.Viewport>;
    $[19] = contentChildren;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== horizontalScrollbar || $[23] !== props || $[24] !== t5 || $[25] !== t7 || $[26] !== verticalScrollbar) {
    t8 = <ScrollAreaPrimitive.Root className={t5} data-slot="scroll-area" {...props}>{t7}{verticalScrollbar}{horizontalScrollbar}</ScrollAreaPrimitive.Root>;
    $[22] = horizontalScrollbar;
    $[23] = props;
    $[24] = t5;
    $[25] = t7;
    $[26] = verticalScrollbar;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
interface ScrollBarProps extends ScrollAreaPrimitive.Scrollbar.Props {
  autoHide?: boolean;
}
function ScrollBar(t0) {
  const $ = _c(17);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      orientation: t1,
      autoHide: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const orientation = t1 === undefined ? "vertical" : t1;
  const autoHide = t2 === undefined ? true : t2;
  const t3 = orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent";
  const t4 = orientation === "horizontal" && "h-2.5 w-full flex-col border-t border-t-transparent";
  let t5;
  if ($[5] !== autoHide) {
    t5 = autoHide && ["opacity-0 transition-opacity duration-200", "data-hovering:opacity-100", "data-scrolling:opacity-100"];
    $[5] = autoHide;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== className || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = cn("flex touch-none select-none p-px", t3, t4, t5, className);
    $[7] = className;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ScrollAreaThumb />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== orientation || $[14] !== props || $[15] !== t6) {
    t8 = <ScrollAreaPrimitive.Scrollbar className={t6} data-slot="scroll-area-scrollbar" orientation={orientation} {...props}>{t7}</ScrollAreaPrimitive.Scrollbar>;
    $[13] = orientation;
    $[14] = props;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function ScrollAreaThumb(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex-1 rounded-full bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ScrollAreaPrimitive.Thumb className={t1} data-slot="scroll-area-thumb" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ScrollAreaCorner(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ScrollAreaPrimitive.Corner className={t1} data-slot="scroll-area-corner" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { ScrollArea, ScrollAreaCorner, ScrollAreaThumb, ScrollBar };